import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileDown, Trash2, CalendarRange, Check, Bell, Users, Wand2 } from "lucide-react";
import { api, apiErr, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
const KIND_TONE = { 研修: "bg-blue-100 text-blue-700", 委員会: "bg-purple-100 text-purple-700", 訓練: "bg-teal-100 text-teal-700" };
const KIND_COLOR = { 研修: "#4ade80", 委員会: "#f97316", 訓練: "#111827" };
const cellColor = (row) => row.color || KIND_COLOR[row.plan_kind] || "#94a3b8";

export default function AnnualPlan() {
  const { fiscalYear } = useFiscalYear();
  const [plans, setPlans] = useState(null);
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState("");
  const [open, setOpen] = useState(false);
  const [cell, setCell] = useState(null); // {row, month} for detail dialog
  const [masters, setMasters] = useState([]);
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", plan_kind: "研修", master_id: "", legal_min_count: 1, company_target_count: 1, scheduled_month: 4, owner: "", note: "" });
  const [roster, setRoster] = useState({});
  const [openRoster, setOpenRoster] = useState(null);
  const [rosterFilter, setRosterFilter] = useState("incomplete");

  const load = useCallback(() => {
    if (!fiscalYear) return;
    let url = `/annual-plans?fiscal_year=${fiscalYear}`;
    if (officeId) url += `&office_id=${officeId}`;
    api.get(url).then(({ data }) => setPlans(data));
  }, [fiscalYear, officeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/offices").then(({ data }) => { setOffices(data); if (data.length && !officeId) setOfficeId(data[0].id); }); }, []);
  useEffect(() => { api.get("/master-trainings/available").then(({ data }) => setMasters(data)).catch(() => {}); }, []);

  const rows = useMemo(() => {
    if (!plans) return [];
    const map = new Map();
    for (const p of plans) {
      if (!map.has(p.name)) map.set(p.name, { name: p.name, plan_kind: p.plan_kind, legal_min_count: p.legal_min_count, color: p.color, order: p.plan_order ?? 999, months: {} });
      const r = map.get(p.name);
      (r.months[p.scheduled_month] ||= []).push(p);
      r.order = Math.min(r.order, p.plan_order ?? 999);
      if (!r.color && p.color) r.color = p.color;
    }
    return [...map.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, "ja"));
  }, [plans]);

  const create = async () => {
    const isTraining = form.plan_kind === "研修";
    if (!officeId) { toast.error("事業所を選択してください"); return; }
    if (isTraining && !form.master_id) { toast.error("研修コンテンツを選択してください"); return; }
    if (!isTraining && !form.name) { toast.error("名称を入力してください"); return; }
    try {
      const { data } = await api.post("/annual-plans", { ...form, master_id: isTraining ? form.master_id : null, fiscal_year: fiscalYear, office_id: officeId });
      toast.success(isTraining ? `研修を追加し、${data.assigned_count ?? 0}名へ受講割当しました` : "計画を追加しました"); setOpen(false);
      setForm({ name: "", plan_kind: "研修", master_id: "", legal_min_count: 1, company_target_count: 1, scheduled_month: 4, owner: "", note: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (pid) => {
    try { await api.delete(`/annual-plans/${pid}`); toast.success("削除しました"); setCell(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const loadRoster = async (tid) => {
    try { const { data } = await api.get(`/assignments?training_id=${tid}`); setRoster((r) => ({ ...r, [tid]: data })); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleRoster = (p) => {
    if (openRoster === p.id) { setOpenRoster(null); return; }
    setOpenRoster(p.id);
    if (!roster[p.training_id]) loadRoster(p.training_id);
  };
  const remind = async (p) => {
    try { const { data } = await api.post(`/trainings/${p.training_id}/remind`); toast.success(`${data.sent}名にリマインドを送信しました`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const quickSetup = async (overwrite = false) => {
    try {
      const { data } = await api.post(`/annual-plans/quick-setup`, { fiscal_year: fiscalYear, overwrite });
      if (data.needs_confirm) {
        if (window.confirm(`${fiscalYear}年度には既に${data.existing}件の計画があります。標準テンプレートで置き換えますか？（既存の割当は作り直されます）`)) {
          return quickSetup(true);
        }
        return;
      }
      toast.success(`標準テンプレート「${data.preset}」から${data.inserted}件を作成し、のべ${data.assigned}名へ割当しました`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reflectAll = async () => {
    if (!window.confirm(`${fiscalYear}年度の未反映の研修をスタッフへまとめて反映します。よろしいですか？`)) return;
    try {
      const { data } = await api.post(`/annual-plans/reflect-all?fiscal_year=${fiscalYear}`);
      toast.success(`${data.reflected}件を反映し、のべ${data.assigned_total}名へ割当しました`);
      if (data.unmatched?.length) toast(`未マッチの研修: ${data.unmatched.join("、")}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reflectOne = async (p) => {
    try { const { data } = await api.post(`/annual-plans/${p.id}/reflect`); toast.success(`スタッフ${data.assigned}名へ反映しました`); setCell(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openAdd = (month, row) => {
    const kind = row?.plan_kind || "研修";
    setForm({
      name: kind === "研修" ? "" : (row?.name || ""), plan_kind: kind, master_id: "",
      legal_min_count: row?.legal_min_count || 1, company_target_count: row?.legal_min_count || 1,
      scheduled_month: month || 4, owner: "", note: "",
    });
    setCell(null);
    setOpen(true);
  };

  if (!plans) return <Loading />;

  return (
    <div data-testid="annual-plan-page">
      <PageHeader
        title="年間計画" subtitle={`${fiscalYear}年度（4月〜翌3月）`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={officeId} onValueChange={setOfficeId}>
              <SelectTrigger className="w-44" data-testid="plan-office-filter"><SelectValue placeholder="事業所" /></SelectTrigger>
              <SelectContent>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" onClick={() => openPdf(`/reports/annual-plan?fiscal_year=${fiscalYear}&office_id=${officeId}`)} data-testid="plan-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF出力</Button>
            <Button variant="outline" onClick={() => quickSetup(false)} data-testid="quick-setup-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50"><Wand2 className="w-4 h-4 mr-1" /> ワンクリック作成</Button>
            <Button variant="outline" onClick={reflectAll} data-testid="reflect-all-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Users className="w-4 h-4 mr-1" /> 未反映をまとめて反映</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => openAdd(4, null)} data-testid="add-plan-btn"><Plus className="w-4 h-4 mr-1" /> 計画を追加</Button>
          </div>
        }
      />

      <div className="flex items-center gap-4 text-xs text-slate-500 mb-3 flex-wrap">
        <span className="flex items-center gap-1.5"><CalendarRange className="w-4 h-4" />セルをクリックして詳細確認・追加ができます</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: KIND_COLOR["委員会"] }} />委員会</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: KIND_COLOR["研修"] }} />研修</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: KIND_COLOR["訓練"] }} />訓練</span>
        <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" />実施済</span>
      </div>

      {rows.length ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="annual-plan-matrix">
          <table className="w-full border-collapse text-sm" style={{ minWidth: 960 }}>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="sticky left-0 z-10 bg-slate-50 text-left font-semibold text-slate-600 px-4 py-3 w-60">研修・訓練内容</th>
                {MONTHS.map((m) => <th key={m} className="text-center font-medium text-slate-500 px-1 py-3 w-16">{m}月</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => {
                const target = row.legal_min_count || 0;
                const done = Object.values(row.months).flat().filter((p) => p.implement_status === "実施済").length;
                const trainPlans = Object.values(row.months).flat().filter((p) => p.training_id);
                const asg = trainPlans.reduce((s, p) => s + (p.assigned_count || 0), 0);
                const cmp = trainPlans.reduce((s, p) => s + (p.completed_count || 0), 0);
                return (
                  <tr key={row.name} className="border-b border-slate-100 hover:bg-slate-50/60 group" data-testid={`plan-row-${ri}`}>
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50/60 px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: cellColor(row) }} />
                        <span className="text-slate-800 leading-tight">{row.name}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${KIND_TONE[row.plan_kind] || "bg-slate-100 text-slate-600"}`}>{row.plan_kind}</span>
                        <span className="text-[10px] text-slate-400">法定{row.legal_min_count}回</span>
                        <span className={`text-[10px] ${done >= target && target > 0 ? "text-emerald-600" : "text-slate-400"}`}>実施 {done}/{target}</span>
                        {trainPlans.length > 0 && <span className={`text-[10px] ${asg > 0 && cmp >= asg ? "text-emerald-600" : "text-slate-400"}`}>受講 {cmp}/{asg}名</span>}
                      </div>
                    </td>
                    {MONTHS.map((m) => {
                      const items = row.months[m] || [];
                      const has = items.length > 0;
                      const allDone = has && items.every((p) => p.implement_status === "実施済");
                      const someDone = has && items.some((p) => p.implement_status === "実施済");
                      return (
                        <td key={m} className="text-center px-1 py-2.5">
                          <button
                            onClick={() => (has ? setCell({ row, month: m }) : openAdd(m, row))}
                            data-testid={`plan-cell-${ri}-${m}`}
                            className={`relative w-9 h-7 rounded-md mx-auto flex items-center justify-center transition-all ${has ? "shadow-sm ring-1 ring-black/5" : "border border-dashed border-slate-200 text-slate-300 hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50/50"}`}
                            style={has ? { background: cellColor(row) } : undefined}
                            title={has ? `${row.name} / ${m}月` : `${m}月に追加`}
                          >
                            {has ? (
                              (allDone || someDone) ? <Check className={`w-4 h-4 ${allDone ? "text-white" : "text-white/70"}`} strokeWidth={3} />
                                : (items.length > 1 ? <span className="text-[10px] font-bold text-white">{items.length}</span> : null)
                            ) : <Plus className="w-3.5 h-3.5" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-dashed border-slate-200 p-12 text-center">
          <CalendarRange className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">この事業所・年度の年間計画がありません</p>
          <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => openAdd(4, null)} data-testid="empty-add-plan-btn"><Plus className="w-4 h-4 mr-1" />計画を追加</Button>
        </div>
      )}

      {/* Cell detail dialog */}
      <Dialog open={!!cell} onOpenChange={(o) => !o && setCell(null)}>
        {cell && (
          <DialogContent data-testid="plan-cell-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: cellColor(cell.row) }} />{cell.row.name}</DialogTitle>
              <DialogDescription>{cell.month}月の予定 · {cell.row.plan_kind}（法定{cell.row.legal_min_count}回）</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {(cell.row.months[cell.month] || []).map((p) => (
                <div key={p.id} className="p-3 rounded-lg bg-slate-50 border border-slate-100" style={{ borderLeft: `3px solid ${cellColor(cell.row)}` }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2"><StatusBadge status={p.implement_status} /><span className="text-xs text-slate-400">担当: {p.owner || "-"}</span></div>
                      {p.note ? <div className="text-xs text-slate-500 mt-1">{p.note}</div> : null}
                      {p.training_id && (
                        <div className="mt-1.5" data-testid={`plan-progress-${p.id}`}>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500">
                            <span>受講状況</span>
                            <span className="font-semibold text-slate-700">{p.completed_count || 0}/{p.assigned_count || 0}名 修了</span>
                            <span className="text-slate-400">（{p.assigned_count ? Math.round((p.completed_count || 0) / p.assigned_count * 100) : 0}%）</span>
                          </div>
                          <div className="mt-1 h-1.5 w-40 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${p.assigned_count ? Math.round((p.completed_count || 0) / p.assigned_count * 100) : 0}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.training_id
                        ? <Button size="sm" variant="outline" onClick={() => navigate(`/trainings/${p.training_id}`)} data-testid={`open-training-${p.id}`}>研修管理</Button>
                        : p.plan_kind === "研修" && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => reflectOne(p)} data-testid={`reflect-${p.id}`}>スタッフに反映</Button>}
                      <button onClick={() => del(p.id)} className="text-slate-300 hover:text-rose-500" data-testid={`del-plan-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {p.training_id && (
                    <div className="mt-2 flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => toggleRoster(p)} data-testid={`roster-toggle-${p.id}`}><Users className="w-3.5 h-3.5 mr-1" />受講者一覧</Button>
                      <Button size="sm" variant="outline" onClick={() => remind(p)} data-testid={`remind-${p.id}`}><Bell className="w-3.5 h-3.5 mr-1" />未修了へリマインド</Button>
                    </div>
                  )}
                  {openRoster === p.id && (
                    <div className="mt-2 border-t border-slate-200 pt-2" data-testid={`roster-${p.id}`}>
                      <Select value={rosterFilter} onValueChange={setRosterFilter}>
                        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`roster-filter-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="incomplete">未修了のみ</SelectItem>
                          <SelectItem value="completed">修了のみ</SelectItem>
                          <SelectItem value="all">全員</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                        {(roster[p.training_id] || [])
                          .filter((a) => rosterFilter === "all" ? true : rosterFilter === "completed" ? a.status === "修了" : a.status !== "修了")
                          .map((a) => (
                            <div key={a.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-white border border-slate-100">
                              <span className="text-slate-700">{a.user_name}</span>
                              <StatusBadge status={a.status} />
                            </div>
                          ))}
                        {!(roster[p.training_id] || []).length && <p className="text-xs text-slate-400">割当がありません</p>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => openAdd(cell.month, cell.row)} data-testid="cell-add-btn"><Plus className="w-4 h-4 mr-1" />この月に追加</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Add plan dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>年間計画を追加</DialogTitle><DialogDescription>研修・委員会・訓練の予定を追加します。</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {form.plan_kind === "研修" ? (
              <div>
                <Label>研修コンテンツ *</Label>
                <Select value={form.master_id} onValueChange={(v) => { const m = masters.find((x) => x.id === v); setForm({ ...form, master_id: v, name: m?.name || "" }); }}>
                  <SelectTrigger className="mt-1" data-testid="plan-master-select"><SelectValue placeholder="マスター教材を選択" /></SelectTrigger>
                  <SelectContent>{masters.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`plan-master-opt-${m.id}`}>{m.name}（{m.category}）</SelectItem>)}</SelectContent>
                </Select>
                {!masters.length && <p className="text-xs text-slate-400 mt-1">利用可能な研修コンテンツがありません（運営にご連絡ください）</p>}
                <p className="text-[11px] text-slate-400 mt-1">選択すると、この事業所で受講できる研修が自動作成されます（動画は運営が管理）</p>
              </div>
            ) : (
              <div><Label>名称 *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="plan-name-input" /></div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>種別</Label>
                <Select value={form.plan_kind} onValueChange={(v) => setForm({ ...form, plan_kind: v, master_id: "", name: v === "研修" ? "" : form.name })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="研修">研修</SelectItem><SelectItem value="委員会">委員会</SelectItem><SelectItem value="訓練">訓練</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>予定月</Label>
                <Select value={String(form.scheduled_month)} onValueChange={(v) => setForm({ ...form, scheduled_month: Number(v) })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{m}月</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>法定最低回数</Label><Input type="number" value={form.legal_min_count} onChange={(e) => setForm({ ...form, legal_min_count: Number(e.target.value) })} className="mt-1" /></div>
              <div><Label>会社目標回数</Label><Input type="number" value={form.company_target_count} onChange={(e) => setForm({ ...form, company_target_count: Number(e.target.value) })} className="mt-1" /></div>
            </div>
            <div><Label>担当者</Label><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="mt-1" /></div>
            <div><Label>備考</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={create} className="bg-blue-600 hover:bg-blue-700" data-testid="save-plan-btn">追加</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
